import type { ComplianceIssue, SuggestedAction } from '@qlk/shared';
import {
  COMPLIANCE_ISSUE_TONE,
  COMPLIANCE_ISSUE_TYPE_LABEL,
  COMPLIANCE_STATUS_LABEL,
  SUGGESTED_ACTIONS,
  SUGGESTED_ACTION_LABEL,
} from '@qlk/shared';
import { ShieldCheck } from 'lucide-react';

import {
  type CotBang,
  DataTable,
  EmptyState,
  MenuThaoTac,
  ngayNgan,
  Nut,
  StatusBadge,
} from '../../../ui/index.js';

/** Trạng thái sự cố dùng cặp màu nào. */
const TONE_TRANG_THAI = {
  needs_response: 'bad',
  disputed: 'info',
  network_support: 'info',
  under_review: 'warn',
  resolved: 'ok',
} as const;

/** Hành động mặc định theo loại sự cố khi `suggestedAction` chưa có. */
const HANH_DONG_THEO_LOAI: Record<ComplianceIssue['type'], SuggestedAction> = {
  content_id_claim: 'dispute',
  internal_music_claim: 'add_whitelist',
  license_expired: 'replace_music',
  ad_limited: 'review_policy',
  copyright_strike: 'dispute',
  community_strike: 'review_policy',
};

export function hanhDongCuaSuCo(suCo: ComplianceIssue): SuggestedAction {
  const goiY = suCo.suggestedAction;
  return goiY && (SUGGESTED_ACTIONS as readonly string[]).includes(goiY)
    ? (goiY as SuggestedAction)
    : HANH_DONG_THEO_LOAI[suCo.type];
}

/** Hạn phản hồi hiển thị `dd/MM`, quá hạn thì đỏ. */
function Han({ dueDate }: { dueDate: string | null }) {
  if (!dueDate) return <span className="text-chu-mo">—</span>;

  const quaHan = new Date(dueDate).getTime() < Date.now();
  const text = ngayNgan(dueDate);

  return (
    <span className={`so-lieu font-semibold ${quaHan ? 'text-bad-chu' : 'text-chu'}`}>
      {text}
      {quaHan && <span className="sr-only"> (đã quá hạn)</span>}
    </span>
  );
}

export interface BangSuCoProps {
  dong: ComplianceIssue[];
  dangTai: boolean;
  conNua: boolean;
  dangTaiThem: boolean;
  onTaiThem: () => void;
  coSua: boolean;
  dangChay: boolean;
  onHanhDong: (suCo: ComplianceIssue, hanhDong: SuggestedAction) => void;
  onDaXuLy: (suCo: ComplianceIssue) => void;
}

/** Bảng "Sự cố đang mở" theo ảnh docs/design/ban-quyen.png. */
export function BangSuCo({
  dong,
  dangTai,
  conNua,
  dangTaiThem,
  onTaiThem,
  coSua,
  dangChay,
  onHanhDong,
  onDaXuLy,
}: BangSuCoProps) {
  const cot: CotBang<ComplianceIssue>[] = [
    {
      key: 'video',
      header: 'Video',
      width: '260px',
      render: (d) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-chu">{d.videoTitle ?? '(chưa rõ video)'}</p>
          {d.videoRef && <p className="so-lieu truncate text-phu text-chu-mo">{d.videoRef}</p>}
        </div>
      ),
    },
    {
      key: 'kenh',
      header: 'Kênh',
      render: (d) => <span className="text-chu-phu">{d.channelName}</span>,
    },
    {
      key: 'loai',
      header: 'Loại',
      render: (d) => (
        <StatusBadge tone={COMPLIANCE_ISSUE_TONE[d.type]} cham={false}>
          {COMPLIANCE_ISSUE_TYPE_LABEL[d.type]}
        </StatusBadge>
      ),
    },
    {
      key: 'claimant',
      header: 'Bên khiếu nại',
      render: (d) => <span className="text-chu-phu">{d.claimant ?? '—'}</span>,
    },
    { key: 'han', header: 'Hạn', align: 'right', render: (d) => <Han dueDate={d.dueDate} /> },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (d) => (
        <StatusBadge tone={TONE_TRANG_THAI[d.status]}>
          {COMPLIANCE_STATUS_LABEL[d.status]}
        </StatusBadge>
      ),
    },
    {
      key: 'hanhDong',
      header: '',
      align: 'right',
      render: (d) => {
        if (!coSua || d.status === 'resolved') return <span className="text-chu-mo">—</span>;
        const hanhDong = hanhDongCuaSuCo(d);

        /*
          Một nút chính mỗi hàng là **hành động gợi ý** của sự cố đó; "Đã xử lý"
          vào ⋯. Bản cũ để hai nút chữ cạnh nhau, và vì nhãn hành động dài
          ("Gia hạn giấy phép") nên cột cuối xuống hai dòng ở mọi hàng.
        */
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Nut
              kieu="phu"
              dangChay={dangChay}
              onClick={() => {
                onHanhDong(d, hanhDong);
              }}
            >
              {SUGGESTED_ACTION_LABEL[hanhDong]}
            </Nut>
            <MenuThaoTac
              aria-label={`Thao tác với sự cố ${d.videoTitle ?? d.channelName}`}
              muc={[
                {
                  nhan: 'Đánh dấu đã xử lý',
                  tat: dangChay,
                  onChon: () => {
                    onDaXuLy(d);
                  },
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      caption="Sự cố bản quyền và chính sách"
      columns={cot}
      rows={dong}
      keyOf={(d) => d.id}
      dangTai={dangTai}
      conNua={conNua}
      dangTaiThem={dangTaiThem}
      onTaiThem={onTaiThem}
      khiRong={
        <EmptyState
          icon={ShieldCheck}
          title="Không có sự cố nào"
          description="Mọi kênh đang an toàn. Bấm “Quét lại tất cả kênh” để kiểm tra lại cảnh báo từ kho nhạc."
        />
      }
    />
  );
}
